import React from 'react';

import type { FC } from 'react';
import { useIntl } from 'react-intl';
import { Installmentplan32 } from '@packages/themes/icons';
import { Box, Divider, Paper, Stack, Typography } from '@packages/shared';
import { Content } from '../Content';
import { PromotionComponentLayout2Messages } from './messages';
import { usePriceFormater } from '../../../../hooks/usePriceFormater';

const offsetCircle = '0.938rem';

export type Layout2Props = {
  /**
   * the single installment value from the calculation result as float (e.g. 13.37)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * the number of installments from the calculation result
   */
  numberOfInstallments: number;
  /**
   * open dialog handler
   */
  openInstallmentCalulatorHandler: () => void;
};

/**
 * Promotion component Layout 2
 *
 * component to promote the installment calculator in layout version 2
 *
 * */
export const Layout2: FC<Layout2Props> = ({
  singleInstallment,
  currency,
  numberOfInstallments,
  openInstallmentCalulatorHandler,
}) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('INSTALLMENTCALCULATOR');

  return (
    <Box mt={offsetCircle} onClick={() => openInstallmentCalulatorHandler()}>
      <Paper
        variant="outlined"
        sx={{
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: `-${offsetCircle}`,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          {/* a white line to have space between cirle and border */}
          <Box
            sx={{
              position: 'absolute',
              top: '0.938rem',
              left: '50%',
              transform: 'translate(-50%, -2px)',
              width: '3.75rem',
              height: '5px',
              backgroundColor: 'common.white',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              width: '2.875rem',
              height: '2.875rem',
              padding: '0.438rem 0.313rem 0.438rem 0.563rem',
              boxSizing: 'border-box',
              backgroundColor: 'primary.main',
              borderRadius: '50%',
            }}
          >
            <Installmentplan32
              sx={{
                width: '2rem',
                height: '2rem',
                fill: (theme) => theme.palette.text.light,
              }}
            />
          </Box>
        </Box>
        <Stack
          direction="column"
          divider={<Divider flexItem orientation="horizontal" />}
          gap={1}
          py={1}
        >
          <Stack direction="row" flexWrap="nowrap" gap={5} px={2}>
            <Content
              smallText={formatMessage(PromotionComponentLayout2Messages.From)}
              bigText={formatPrice(singleInstallment, currency)}
            />
            <Content
              smallText={formatMessage(PromotionComponentLayout2Messages.InstallmentsPrefix)}
              bigText={formatMessage(PromotionComponentLayout2Messages.Installments, {
                installments: numberOfInstallments,
              })}
            />
          </Stack>
          <Box>
            <Typography variant="body2" textAlign="center" sx={{ cursor: 'pointer' }}>
              {formatMessage(PromotionComponentLayout2Messages.CtA)}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};
